import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  TextField,
  Button,
  IconButton,
  Drawer,
  Badge,
  CircularProgress,
  MenuItem,
  Divider,
  Fade,
  Slide,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  ShoppingCart as CartIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  DeleteOutline as DeleteIcon,
  CheckCircleOutline as SuccessIcon,
  Storefront as StoreIcon,
  Close as CloseIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { getPublicStoreInventory, createPickupOrder, StoreListing } from '../../services/storeService';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface CartItem {
  listing: StoreListing;
  quantity: number;
}

const Storefront: React.FC = () => {
  const theme = useTheme();
  const { showNotification } = useNotification();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Search & Filter State
  const [listings, setListings] = useState<StoreListing[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [gameFilter, setGameFilter] = useState<string>('all');
  const [conditionFilter, setConditionFilter] = useState<string>('all');

  // Cart State
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState<boolean>(false);

  // Checkout Form State
  const [customerName, setCustomerName] = useState<string>('');
  const [customerEmail, setCustomerEmail] = useState<string>('');
  const [customerPhone, setCustomerPhone] = useState<string>('');
  const [placingOrder, setPlacingOrder] = useState<boolean>(false);

  // Success State
  const [successOrder, setSuccessOrder] = useState<any | null>(null);

  // Details modal functionality removed in favor of direct navigation to the card details page

  // Load inventory
  const loadInventory = async () => {
    setLoading(true);
    try {
      const activeListings = await getPublicStoreInventory({
        game: gameFilter !== 'all' ? gameFilter : undefined,
        search: searchQuery || undefined,
      });
      setListings(activeListings);
    } catch (error: any) {
      showNotification('Failed to load store listings', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInventory();
  }, [gameFilter, searchQuery]);

  // Load cart from localStorage
  useEffect(() => {
    const savedCart = localStorage.getItem('chasingtcg_cart');
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (e) {
        console.error('Failed to parse saved cart');
      }
    }
  }, []);

  // Save cart to localStorage
  const saveCart = (newCart: CartItem[]) => {
    setCart(newCart);
    localStorage.setItem('chasingtcg_cart', JSON.stringify(newCart));
  };

  // Add to cart
  const handleAddToCart = (listing: StoreListing) => {
    const existingItem = cart.find((item) => item.listing.listingId === listing.listingId);
    
    if (existingItem) {
      if (existingItem.quantity >= listing.stockQuantity) {
        showNotification(`Only ${listing.stockQuantity} items are available in stock`, 'warning');
        return;
      }
      const newCart = cart.map((item) =>
        item.listing.listingId === listing.listingId
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
      saveCart(newCart);
    } else {
      saveCart([...cart, { listing, quantity: 1 }]);
    }
    showNotification(`Added ${listing.cardName} to cart`, 'success');
  };

  // Adjust cart item quantity
  const handleAdjustQuantity = (listingId: string, delta: number) => {
    const item = cart.find((i) => i.listing.listingId === listingId);
    if (!item) return;

    const newQuantity = item.quantity + delta;
    if (newQuantity <= 0) {
      handleRemoveItem(listingId);
      return;
    }

    if (newQuantity > item.listing.stockQuantity) {
      showNotification(`Only ${item.listing.stockQuantity} items in stock`, 'warning');
      return;
    }

    const newCart = cart.map((i) =>
      i.listing.listingId === listingId ? { ...i, quantity: newQuantity } : i
    );
    saveCart(newCart);
  };

  // Remove from cart
  const handleRemoveItem = (listingId: string) => {
    const newCart = cart.filter((i) => i.listing.listingId !== listingId);
    saveCart(newCart);
    showNotification('Item removed from cart', 'info');
  };

  // Cart helper calculations
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartSubtotal = cart.reduce((sum, item) => sum + item.listing.price * item.quantity, 0);

  // Place order
  const handleCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerName || !customerEmail || !customerPhone) {
      showNotification('Please fill in all contact information', 'warning');
      return;
    }

    setPlacingOrder(true);
    try {
      const order = await createPickupOrder({
        customerName,
        customerEmail,
        customerPhone,
        items: cart.map((item) => ({
          listingId: item.listing.listingId,
          quantity: item.quantity,
        })),
      });

      setSuccessOrder(order);
      saveCart([]); // Clear cart
      setCustomerName('');
      setCustomerEmail('');
      setCustomerPhone('');
      setIsCartOpen(false);
      showNotification('Order placed successfully!', 'success');
    } catch (error: any) {
      showNotification(error.message || 'Failed to place pickup order', 'error');
    } finally {
      setPlacingOrder(false);
    }
  };

  return (
    <Box sx={{ minHeight: '85vh', pb: 8 }}>
      {/* Header section with styling */}
      <Box
        sx={{
          mb: 6,
          p: 4,
          borderRadius: 4,
          background: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 3,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <StoreIcon color="primary" /> Retail Storefront
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Browse our available retail inventory, compile your cart, and place orders for physical store pickup.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setIsCartOpen(true)}
            startIcon={
              <Badge badgeContent={totalItems} color="error">
                <CartIcon />
              </Badge>
            }
            sx={{
              py: 1.5,
              px: 3,
              borderRadius: 3,
              boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.25)}`,
              transition: 'transform 0.2s',
              '&:hover': { transform: 'translateY(-2px)' },
            }}
          >
            View Shopping Cart
          </Button>
        </Box>
      </Box>

      {/* Filter and Search Bar */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={5}>
          <TextField
            fullWidth
            placeholder="Search by card name, set, or card number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.02)',
                backdropFilter: 'blur(8px)',
              },
            }}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            select
            fullWidth
            label="Game Type"
            value={gameFilter}
            onChange={(e) => setGameFilter(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.02)',
              },
            }}
          >
            <MenuItem value="all">All Games</MenuItem>
            <MenuItem value="pokemon">Pokémon TCG</MenuItem>
            <MenuItem value="onepiece">One Piece Card Game</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={6} md={4} sx={{ display: 'flex', gap: 2 }}>
          <TextField
            select
            fullWidth
            label="Card Condition"
            value={conditionFilter}
            onChange={(e) => setConditionFilter(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.02)',
              },
            }}
          >
            <MenuItem value="all">All Conditions</MenuItem>
            <MenuItem value="NM">Near Mint (NM)</MenuItem>
            <MenuItem value="LP">Lightly Played (LP)</MenuItem>
            <MenuItem value="MP">Moderately Played (MP)</MenuItem>
            <MenuItem value="HP">Heavily Played (HP)</MenuItem>
            <MenuItem value="G">Graded</MenuItem>
          </TextField>
          <Button
            variant="outlined"
            onClick={loadInventory}
            sx={{ borderRadius: 3, minWidth: 'fit-content' }}
          >
            Refresh
          </Button>
        </Grid>
      </Grid>

      {/* Main Grid View */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 12 }}>
          <CircularProgress size={50} />
        </Box>
      ) : listings.length === 0 ? (
        <Box
          sx={{
            py: 12,
            textAlign: 'center',
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.01)',
            border: '1px dashed rgba(255, 255, 255, 0.05)',
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No active listings found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Adjust your search terms or filters above to discover cards.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {listings
            .filter((l) => conditionFilter === 'all' || l.condition === conditionFilter)
            .map((listing) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={listing.listingId}>
                <Card
                  onClick={() => {
                    if (listing.game === 'pokemon') {
                      navigate(`/sets/${listing.setId}/cards/${listing.cardId}`, {
                        state: { fromSearch: true, searchUrl: '/store' }
                      });
                    } else {
                      navigate(`/onepiece/sets/${listing.setId}/cards/${listing.cardId}`, {
                        state: { fromSearch: true, searchUrl: '/store' }
                      });
                    }
                  }}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'rgba(255, 255, 255, 0.02)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    borderRadius: 4,
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: `0 12px 30px rgba(0,0,0,0.4)`,
                      borderColor: alpha(theme.palette.primary.main, 0.2),
                      background: 'rgba(255, 255, 255, 0.04)',
                    },
                  }}
                >
                  <Box sx={{ position: 'relative', pt: '135%', overflow: 'hidden' }}>
                    <CardMedia
                      image={listing.imageUrl}
                      title={listing.cardName}
                      component="div"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        p: 1,
                      }}
                    />
                    {/* Floating Labels */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        left: 12,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}
                    >
                      <Badge
                        badgeContent={listing.game === 'pokemon' ? 'Pokémon' : 'One Piece'}
                        color={listing.game === 'pokemon' ? 'primary' : 'secondary'}
                        sx={{
                          '& .MuiBadge-badge': {
                            position: 'static',
                            transform: 'none',
                            fontWeight: 'bold',
                            px: 1.5,
                            py: 1,
                            borderRadius: 1,
                          },
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        background: 'rgba(0,0,0,0.6)',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1.5,
                        backdropFilter: 'blur(4px)',
                      }}
                    >
                      <Typography variant="caption" fontWeight="bold">
                        {listing.condition}
                      </Typography>
                    </Box>
                  </Box>

                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: 3 }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" noWrap>
                        {listing.cardName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {listing.setName} • #{listing.number}
                      </Typography>
                    </Box>

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" color="primary">
                          ${listing.price.toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color={listing.stockQuantity > 0 ? 'text.secondary' : 'error.main'}>
                          {listing.stockQuantity > 0 ? `${listing.stockQuantity} in stock` : 'Out of stock'}
                        </Typography>
                      </Box>

                      <Button
                        variant="contained"
                        size="small"
                        disabled={listing.stockQuantity <= 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddToCart(listing);
                        }}
                        sx={{ borderRadius: 2 }}
                        startIcon={<AddIcon />}
                      >
                        Add
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>
      )}

      {/* Cart Drawer */}
      <Drawer
        anchor="right"
        open={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 460 },
            background: 'rgba(15, 15, 15, 0.95)',
            backdropFilter: 'blur(20px)',
            borderLeft: '1px solid rgba(255, 255, 255, 0.08)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Drawer Header */}
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
          <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CartIcon /> Shopping Cart
          </Typography>
          <IconButton onClick={() => setIsCartOpen(false)} sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Drawer Content */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3 }}>
          {cart.length === 0 ? (
            <Box sx={{ textAlignment: 'center', py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                Your shopping cart is empty.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Go list some cards or add active listings to get started!
              </Typography>
            </Box>
          ) : (
            <Box>
              {cart.map((item) => (
                <Box
                  key={item.listing.listingId}
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 3,
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.05)',
                    display: 'flex',
                    gap: 2,
                  }}
                >
                  <img
                    src={item.listing.imageUrl}
                    alt={item.listing.cardName}
                    style={{ width: 60, height: 82, objectFit: 'contain', borderRadius: 4 }}
                  />

                  <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" fontWeight="bold" noWrap>
                        {item.listing.cardName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {item.listing.setName} • {item.listing.condition}
                      </Typography>
                      <Typography variant="caption" color="primary.main" fontWeight="bold">
                        ${item.listing.price.toFixed(2)} each
                      </Typography>
                    </Box>

                    {/* Quantity controls */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, background: 'rgba(0,0,0,0.3)', borderRadius: 2, px: 0.5 }}>
                        <IconButton size="small" onClick={() => handleAdjustQuantity(item.listing.listingId, -1)}>
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 20, textAlign: 'center' }}>
                          {item.quantity}
                        </Typography>
                        <IconButton size="small" onClick={() => handleAdjustQuantity(item.listing.listingId, 1)}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <IconButton size="small" color="error" onClick={() => handleRemoveItem(item.listing.listingId)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              ))}

              <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.08)' }} />

              {/* Checkout Form */}
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                In-Store Pickup Details
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 3 }}>
                You will settle payment directly at the store during pickup. Enter your details below.
              </Typography>

              <form onSubmit={handleCheckout}>
                <TextField
                  fullWidth
                  required
                  label="Full Name"
                  size="small"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  required
                  type="email"
                  label="Email Address"
                  size="small"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  required
                  label="Phone Number"
                  size="small"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  sx={{ mb: 3 }}
                />

                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.02)',
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    mb: 3,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Items Subtotal</Typography>
                    <Typography variant="body2" fontWeight="bold">${cartSubtotal.toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle1" fontWeight="bold">Total Reservation Amount</Typography>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary">${cartSubtotal.toFixed(2)}</Typography>
                  </Box>
                </Box>

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={placingOrder}
                  sx={{ py: 1.5, borderRadius: 3, fontWeight: 'bold' }}
                >
                  {placingOrder ? <CircularProgress size={24} /> : `Confirm Pickup Order ($${cartSubtotal.toFixed(2)})`}
                </Button>
              </form>
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Success Modal */}
      <Dialog
        open={!!successOrder}
        onClose={() => setSuccessOrder(null)}
        PaperProps={{
          sx: {
            background: 'rgba(20, 20, 20, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: 4,
            p: 3,
            maxWidth: 480,
            textAlign: 'center',
          },
        }}
      >
        <DialogContent>
          <SuccessIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Order Placed Successfully!
          </Typography>
          <Typography variant="subtitle1" color="primary" fontWeight="bold" sx={{ mb: 2 }}>
            Order ID: {successOrder?.orderId}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Thank you, <strong>{successOrder?.customerName}</strong>! Your order is being prepped. We've sent a confirmation to <strong>{successOrder?.customerEmail}</strong>.
          </Typography>

          <Box
            sx={{
              background: 'rgba(255, 255, 255, 0.02)',
              p: 2,
              borderRadius: 3,
              border: '1px solid rgba(255, 255, 255, 0.05)',
              textAlign: 'left',
              mb: 3,
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Pickup Instructions:
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              • Bring your Order ID to the physical store.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              • Settle your total of <strong>${successOrder?.totalAmount?.toFixed(2)}</strong> during pickup.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Once the store finishes prepping your order, you'll receive a ready-for-pickup notification.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button variant="contained" onClick={() => setSuccessOrder(null)} sx={{ px: 4, borderRadius: 2 }}>
            Back to Storefront
          </Button>
        </DialogActions>
      </Dialog>


    </Box>
  );
};

export default Storefront;
